/**
 * Multi-language route mapping.
 * Each route key maps to localized path suffixes for all supported languages.
 */
import type { SupportedLang } from './LanguageProvider';

const ROUTE_PATHS: Record<string, Record<SupportedLang, string>> = {
  'how-it-works': {
    en: '/how-it-works',
    de: '/so-funktionierts',
    it: '/come-funziona',
    es: '/como-funciona',
    fr: '/comment-ca-marche',
  },
  'use-cases': {
    en: '/use-cases',
    de: '/anwendungsfaelle',
    it: '/casi-duso',
    es: '/casos-de-uso',
    fr: '/cas-dusage',
  },
  security: {
    en: '/security',
    de: '/sicherheit',
    it: '/sicurezza',
    es: '/seguridad',
    fr: '/securite',
  },
  pricing: {
    en: '/pricing',
    de: '/pricing',
    it: '/pricing',
    es: '/pricing',
    fr: '/pricing',
  },
  contact: {
    en: '/contact',
    de: '/kontakt',
    it: '/contatto',
    es: '/contacto',
    fr: '/contact',
  },
  'early-access': {
    en: '/early-access',
    de: '/early-access',
    it: '/early-access',
    es: '/early-access',
    fr: '/early-access',
  },
  imprint: {
    en: '/imprint',
    de: '/impressum',
    it: '/note-legali',
    es: '/aviso-legal',
    fr: '/mentions-legales',
  },
  privacy: {
    en: '/privacy',
    de: '/datenschutz',
    it: '/privacy-policy',
    es: '/privacidad',
    fr: '/confidentialite',
  },
  'thank-you': {
    en: '/thank-you',
    de: '/danke',
    it: '/grazie',
    es: '/gracias',
    fr: '/merci',
  },
};

/** Convert a route suffix from one language to another */
export function convertPath(
  path: string,
  fromLang: SupportedLang,
  toLang: SupportedLang,
): string {
  for (const routes of Object.values(ROUTE_PATHS)) {
    if (path === routes[fromLang]) return routes[toLang];
    if (path.startsWith(`${routes[fromLang]}/`)) {
      return routes[toLang] + path.slice(routes[fromLang].length);
    }
  }
  return path;
}

/** Get all route suffixes for a given language */
export function getRoutesForLang(lang: SupportedLang): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, routes] of Object.entries(ROUTE_PATHS)) {
    result[key] = routes[lang];
  }
  return result;
}

// Legacy exports for backward compatibility with tests
export function dePathToEn(dePath: string): string {
  return convertPath(dePath, 'de', 'en');
}

export function enPathToDe(enPath: string): string {
  return convertPath(enPath, 'en', 'de');
}

export const EN_ROUTES = Object.values(ROUTE_PATHS).map((r) => r.en);
export const DE_ROUTES = Object.values(ROUTE_PATHS).map((r) => r.de);
